import { createFileRoute, useNavigate, useSearch, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/auth/AuthProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { ArrowLeft, Loader2 } from "lucide-react";

type Search = { clientId?: string; contractId?: string };

export const Route = createFileRoute("/_app/projects/new")({
  component: NewProjectPage,
  validateSearch: (s: Record<string, unknown>): Search => ({
    clientId: typeof s.clientId === "string" ? s.clientId : undefined,
    contractId: typeof s.contractId === "string" ? s.contractId : undefined,
  }),
});

type Client = { id: string; name: string };
type Contract = {
  id: string;
  number: string;
  title: string;
  client_id: string;
  amount: number;
  currency: string;
  scope: string | null;
  notes: string | null;
  start_date: string | null;
  end_date: string | null;
  status: string;
};

function NewProjectPage() {
  const { t } = useTranslation();
  const { company, profile } = useAuth();
  const navigate = useNavigate();
  const search = useSearch({ from: "/_app/projects/new" });

  const [clients, setClients] = useState<Client[]>([]);
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [clientId, setClientId] = useState(search.clientId ?? "");
  const [contractId, setContractId] = useState(search.contractId ?? "");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [status, setStatus] = useState<
    "planning" | "active" | "on_hold" | "completed" | "cancelled"
  >("planning");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [budget, setBudget] = useState<number>(0);
  const [currency, setCurrency] = useState(company?.currency ?? "USD");
  const [expectedClockIn, setExpectedClockIn] = useState("");
  const [expectedClockOut, setExpectedClockOut] = useState("");
  const [clockGrace, setClockGrace] = useState<string>("30");
  const [clockAlertsEnabled, setClockAlertsEnabled] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      const [c, k] = await Promise.all([
        supabase.from("clients").select("id, name").eq("is_active", true).order("name"),
        supabase
          .from("contracts")
          .select(
            "id, number, title, client_id, amount, currency, scope, notes, start_date, end_date, status",
          )
          .order("created_at", { ascending: false }),
      ]);
      setClients((c.data as Client[]) ?? []);
      setContracts((k.data as Contract[]) ?? []);
    })();
  }, []);

  useEffect(() => {
    if (!contractId) return;
    const c = contracts.find((x) => x.id === contractId);
    if (!c) return;
    setClientId(c.client_id);
    setName(c.title);
    setBudget(Number(c.amount) || 0);
    setCurrency(c.currency || "USD");
    setDescription([c.scope, c.notes].filter(Boolean).join("\n\n"));
    setStartDate(c.start_date ?? "");
    setEndDate(c.end_date ?? "");
    if (c.status === "signed" || c.status === "active") setStatus("active");
    else if (c.status === "completed") setStatus("completed");
    else if (c.status === "cancelled") setStatus("cancelled");
    else setStatus("planning");
  }, [contractId, contracts]);

  const filteredContracts = clientId
    ? contracts.filter((c) => c.client_id === clientId)
    : contracts;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!company || !profile) return;
    if (!clientId) return toast.error(t("newProject.selectClientErr"));
    if (!name.trim()) return toast.error(t("newProject.nameRequired"));
    setSaving(true);
    const { data, error } = await supabase
      .from("projects")
      .insert({
        company_id: company.id,
        client_id: clientId,
        contract_id: contractId || null,
        created_by: profile.id,
        code: "",
        name: name.trim(),
        description: description.trim() || null,
        status,
        start_date: startDate || null,
        end_date: endDate || null,
        budget,
        currency,
        expected_clock_in_time: expectedClockIn || null,
        expected_clock_out_time: expectedClockOut || null,
        clock_alert_grace_minutes: clockGrace ? Math.max(0, Number(clockGrace)) : 30,
        clock_alerts_enabled: clockAlertsEnabled,
      } as never)
      .select("id")
      .single();
    setSaving(false);
    if (error) return toast.error(error.message);
    toast.success(t("newProject.createdToast"));
    navigate({ to: "/projects/$projectId", params: { projectId: data!.id } });
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/projects">
            <ArrowLeft className="mr-1 h-4 w-4" />
            {t("newProject.back")}
          </Link>
        </Button>
        <h1 className="text-2xl font-semibold tracking-tight">{t("newProject.title")}</h1>
      </div>

      <form
        onSubmit={onSubmit}
        className="grid max-w-3xl gap-5 rounded-lg border bg-background p-6"
      >
        <div className="grid gap-2 sm:grid-cols-2">
          <div className="space-y-2">
            <Label>{t("newProject.client")}</Label>
            <Select value={clientId} onValueChange={setClientId}>
              <SelectTrigger>
                <SelectValue placeholder={t("newProject.selectClient")} />
              </SelectTrigger>
              <SelectContent>
                {clients.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>{t("newProject.contract")}</Label>
            <Select
              value={contractId || "none"}
              onValueChange={(v) => setContractId(v === "none" ? "" : v)}
            >
              <SelectTrigger>
                <SelectValue placeholder={t("newProject.linkContract")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">{t("newProject.noContract")}</SelectItem>
                {filteredContracts.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.number} — {c.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-2">
          <Label>{t("newProject.name")}</Label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t("newProject.namePlaceholder")}
          />
        </div>

        <div className="space-y-2">
          <Label>{t("newProject.description")}</Label>
          <Textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            placeholder={t("newProject.descriptionPlaceholder")}
          />
        </div>

        <div className="grid gap-3 sm:grid-cols-3">
          <div className="space-y-2">
            <Label>{t("newProject.status")}</Label>
            <Select value={status} onValueChange={(v) => setStatus(v as typeof status)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="planning">{t("newProject.statusOptions.planning")}</SelectItem>
                <SelectItem value="active">{t("newProject.statusOptions.active")}</SelectItem>
                <SelectItem value="on_hold">{t("newProject.statusOptions.on_hold")}</SelectItem>
                <SelectItem value="completed">{t("newProject.statusOptions.completed")}</SelectItem>
                <SelectItem value="cancelled">{t("newProject.statusOptions.cancelled")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>{t("newProject.startDate")}</Label>
            <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label>{t("newProject.endDate")}</Label>
            <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-2">
            <Label>{t("newProject.budget")}</Label>
            <Input
              type="number"
              min={0}
              step="0.01"
              value={budget}
              onChange={(e) => setBudget(Number(e.target.value))}
            />
          </div>
          <div className="space-y-2">
            <Label>{t("newProject.currency")}</Label>
            <Select value={currency} onValueChange={setCurrency}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {["USD", "EUR", "GBP", "BRL", "CAD", "AUD", "MXN"].map((c) => (
                  <SelectItem key={c} value={c}>
                    {c}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="rounded-md border bg-muted/30 p-4">
          <div className="mb-1 flex items-center justify-between gap-3">
            <p className="text-sm font-medium">{t("newProject.clockSchedule")}</p>
            <div className="flex items-center gap-2">
              <Switch
                checked={clockAlertsEnabled}
                onCheckedChange={setClockAlertsEnabled}
                id="clock-alerts-enabled"
              />
              <Label htmlFor="clock-alerts-enabled" className="text-xs font-normal">
                {t("newProject.clockAlertsEnabled")}
              </Label>
            </div>
          </div>
          <p className="mb-3 text-xs text-muted-foreground">{t("newProject.clockScheduleHint")}</p>
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="space-y-2">
              <Label>{t("newProject.expectedClockIn")}</Label>
              <Input
                type="time"
                value={expectedClockIn}
                onChange={(e) => setExpectedClockIn(e.target.value)}
                disabled={!clockAlertsEnabled}
              />
            </div>
            <div className="space-y-2">
              <Label>{t("newProject.expectedClockOut")}</Label>
              <Input
                type="time"
                value={expectedClockOut}
                onChange={(e) => setExpectedClockOut(e.target.value)}
                disabled={!clockAlertsEnabled}
              />
            </div>
            <div className="space-y-2">
              <Label>{t("newProject.clockGrace")}</Label>
              <Input
                type="number"
                min={0}
                value={clockGrace}
                onChange={(e) => setClockGrace(e.target.value)}
                disabled={!clockAlertsEnabled}
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" asChild>
            <Link to="/projects">{t("newProject.cancel")}</Link>
          </Button>
          <Button type="submit" disabled={saving}>
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t("newProject.create")}
          </Button>
        </div>
      </form>
    </div>
  );
}
